import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { NavLink } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import Publication from "../../components/Publication/Publication";
import ContactUsBanner from "../../components/ContactUsBanner/ContactUsBanner";
import {
  imgArtallBg,
  imgCambridgeBg,
  imgFoBg,
  imgReBg,
  imgTechBg,
  imgThinktankBg,
  imgVclubBg,
  videoHomeBg,
  videoHomeBgPoster,
} from "../../assets/assetsExport";
import { useSelector } from "react-redux";
import { getIsSmallScreen, getLang } from "../../features/app/appSlice";
import SlideUpAnimation from "../../components/SlideUpAnimation/SlideUpAnimation";
import Partnerships from "../../components/Partnerships/Partnerships";
import SchemaMarkup from "../../components/SchemaMarkup/SchemaMarkup";
import NewsHighlightSection from "../../components/News/NewsHighlightSection";

const HomePage: React.FC = () => {
  const lang = useSelector(getLang);
  const isSmallScreen = useSelector(getIsSmallScreen);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.6;
    }
  }, []);

  function HeroSection() {
    return (
      <div className="h-screen w-screen relative overflow-hidden">
        <div>
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-screen h-screen object-cover"
            src={videoHomeBg}
            autoPlay
            loop
            muted
            playsInline
            poster={videoHomeBgPoster}
          />
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-black/40 via-black/55 to-black/85"></div>
        </div>

        <div className="relative h-full w-full flex flex-col items-start p-6 mobile:p-10 sm:p-14 gap-6 sm:gap-10 text-left justify-end">
          <SlideUpAnimation>
            <div>
              <h1 className="text-white leading-[1.05] font-poster text-5xl mobile:text-6xl sm:text-7xl text-left">
                Global Vision
                <br />
                Local Expertise
              </h1>
              {!lang && (
                <p className="font-header text-main/90 text-2xl mt-10 sm:mt-12 tracking-wide">
                  国际视野，本地专识
                </p>
              )}
            </div>
          </SlideUpAnimation>

          <div className="w-24 h-px bg-white/40 my-1"></div>
          <p className="text-white/80 sm:max-w-[680px] text-left leading-8 text-[15px]">
            {lang
              ? "New Vision International Group (NVIG) is a London-based global asset allocation platform, providing comprehensive, customised solutions for businesses, high-net-worth individuals, and families seeking international growth and diversification."
              : "新愿景国际集团 New Vision International Group (NVIG)，总部位于英国伦敦，是全球资产配置专业平台，为有全球化综合发展需求的企业及高净值人士和家族提供一站式定制服务。"}
          </p>
        </div>
      </div>
    );
  }

  function WhatWeDoSection() {
    function Section({
      to,
      style,
      img,
      title,
      subtitle,
      description,
      isWide,
      index,
    }: {
      to: string;
      style?: any;
      img: any;
      title: string;
      subtitle: string;
      description: string;
      isWide?: boolean;
      index: number;
    }) {
      return (
        <NavLink to={to} style={style} className="group block">
          <SlideUpAnimation>
            <div className="flex items-baseline justify-between">
              <p className="font-header text-lg sm:text-xl text-secondary-dark group-hover:text-main transition-colors">
                {title}
              </p>
              <span className="font-poster text-main text-base tracking-widest">
                {String(index + 1).padStart(2, "0")}
              </span>
            </div>
            <div className="w-full h-px bg-secondary-dark/30 mt-2 mb-4 group-hover:bg-main transition-colors"></div>
            <div className={`flex ${isWide ? "flex-row" : "flex-col"}`}>
              <div
                className={`${
                  isWide ? "w-1/2 sm:pr-2 lg:pr-4" : "w-full"
                } overflow-hidden`}
              >
                <img
                  src={img}
                  alt={subtitle + " image"}
                  className="w-full h-60 object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className={isWide ? "w-1/2 sm:pl-2 lg:pl-4" : "w-full"}>
                <p className="font-header text-2xl sm:text-[26px] leading-snug min-h-[3.5rem] mt-3 mb-4 text-secondary-dark">
                  {subtitle}
                </p>
                <p className="text-[14.5px] leading-7 text-secondary-dark/80">
                  {description}
                </p>
              </div>
            </div>
          </SlideUpAnimation>
        </NavLink>
      );
    }

    const sections = [
      {
        img: imgFoBg,
        to: "/business/family-office",
        title: { en: "Family Office", cn: "家族办公室" },
        subtitle: {
          en: "Bespoke Wealth Management & Legacy Planning",
          cn: "定制化财富管理与传承规划",
        },
        description: {
          en: "We offer bespoke services for ultra-high-net-worth Chinese families, including global asset allocation, wealth inheritance, tax planning, trust structuring, and identity planning. We are committed to professional planning and stable growth, supporting the intergenerational transfer and global positioning of family wealth.",
          cn: "秉承“洞悉时局变迁，专业规划配置，稳定增值传延”的理念，为诸多中国的超高净值家族提供一站式全球资产配置、身份和教育规划、财富管理传承和私人银行等服务。",
        },
      },
      {
        img: imgReBg,
        to: "/business/real-estate",
        title: { en: "Real Estate", cn: "房地产" },
        subtitle: {
          en: "Exclusive Property Investment & Advisory",
          cn: "独家房产投资与咨询",
        },
        description: {
          en: "Headquartered in London, our high-end real estate consultancy is ARLA Propertymark certified, specializing in premium real estate investments for high-net-worth individuals. We offer high-end property investment, acquisition, leasing, and management services, based on in-depth market analysis and strategic insights.",
          cn: "总部位于伦敦的精品地产咨询公司，受ARLA Propertymark认证，专注于高净值人士的优质地产投资。提供高端房产投资、收购、租赁和管理服务，基于深入的市场分析和战略见解。",
        },
      },
      {
        img: imgTechBg,
        to: "/business/technology-investment",
        title: { en: "Technology Investment", cn: "科技投资" },
        subtitle: {
          en: "UK Innovation & Global Capital",
          cn: "英国创新与全球资本",
        },
        description: {
          en: "A London-based technology investment consulting platform and business accelerator, linking UK university tech enterprises with the Chinese capital market.",
          cn: "为英国顶尖高校衍生的优质科技企业有效对接资本及市场，并且为高净值投资人，家族企业及跨境基金提供科技项目投资的商业咨询及本地化管理服务。",
        },
      },
      {
        img: imgArtallBg,
        to: "/business/art-culture",
        title: { en: "Art and Culture", cn: "艺术与文化" },
        subtitle: {
          en: "Elite Art Advisory & Cultural Exchange",
          cn: "精英艺术咨询与文化交流",
        },
        description: {
          en: "A London-based art and culture communication company, offering high-end art collection and investment advisory services, and promoting cultural exchanges between the East and the West.",
          cn: "作为东西方艺术文化交流的桥梁，直接对接国际知名的博物馆、艺术院校等机构，举办艺术展览、文化论坛等一系列线上线下活动，涉及领域包括中国书画、古陶瓷、茶文化和宗教艺术等。 ",
        },
      },
      {
        img: imgThinktankBg,
        to: "/business/think-tank",
        title: { en: "Think Tank", cn: "智库" },
        subtitle: {
          en: "Global Wealth Management Insights",
          cn: "全球资产配置洞察",
        },
        description: {
          en: "Facilitating wise decision-making through comprehensive research on global asset allocation, collaborating with renowned scholars and industry experts to provide practical insights into wealth management.",
          cn: "秉承“汇聚全球精英，共享持续发展”的理念，保持“客观、深度、致用”的态度，专注于提高业界对全球资产配置和财富管理最佳实践的知识。",
        },
      },
      {
        img: imgVclubBg,
        to: "/business/private-club",
        title: { en: "Private Club", cn: "私人俱乐部" },
        subtitle: {
          en: "Exclusive Private Lifestyle & Networking",
          cn: "独家私人生活方式与社交",
        },
        description: {
          en: "V Club is a high-end private club in London, offering a refined social and interest platform for like-minded high-net-worth individuals, fostering exclusive connections and sophisticated experiences within London's most prestigious circles.",
          cn: "高端生活方式私人俱乐部，为高净值人士的社交与兴趣发展提供聚合与交流平台。 ",
        },
      },
      {
        img: imgCambridgeBg,
        to: "/business/cambridge-foundation",
        title: { en: "Cambridge Foundation", cn: "剑桥基金会" },
        subtitle: {
          en: "Academic Excellence & Philanthropic Impact",
          cn: "学术卓越与慈善影响",
        },
        description: {
          en: "New Vision Cambridge Foundation is a corporate social responsibility initiative of the group. It supports academic excellence and philanthropy by promoting groundbreaking research, global collaboration, and transformative impact through projects led by Cambridge.",
          cn: "新愿景剑桥基金会 (New Vision Cambridge Foundation)，是集团企业社会责任感公益项目。支持学术卓越和慈善事业，通过剑桥主导的项目促进突破性研究、全球合作和变革性影响。",
        },
      },
    ];

    return (
      <div>
        <div>
          <p className="tag">{lang ? "WHAT WE DO" : "关于我们"}</p>
          <h2 className="header-border-t mt-3">
            {lang ? "Delivering for Our Client" : "为客户提供服务"}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:gap-x-6 lg:gap-x-10 gap-y-20 mt-14">
          {sections.map((i, index) => {
            return (
              <Section
                to={i.to}
                img={i.img}
                title={lang ? i.title.en : i.title.cn}
                subtitle={lang ? i.subtitle.en : i.subtitle.cn}
                description={lang ? i.description.en : i.description.cn}
                isWide={index === 0 && !isSmallScreen}
                style={index === 0 ? { gridColumn: "1 / -1" } : {}}
                index={index}
              />
            );
          })}
        </div>
      </div>
    );
  }

  function PublicationSection() {
    return <Publication withTitle={true} />;
  }

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "New Vision International Group",
    alternateName: "NVIG",
    legalName: "New Vision International Group Limited",
    url: "https://www.newvgroup.com",
    logo: "https://www.newvgroup.com/logo192.png",
    description:
      "New Vision International Group (NVIG) is a London-based global asset allocation platform, providing comprehensive, customised solutions for businesses, high-net-worth individuals, and families seeking international growth and diversification.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "16 Berkeley Street, Mayfair",
      addressLocality: "London",
      postalCode: "W1J 8DZ",
      addressCountry: "GB",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+44-20-3907-4025",
      contactType: "customer service",
      email: "info@newvgroup.com",
    },
    foundingLocation: {
      "@type": "Place",
      name: "London, United Kingdom",
    },
  };

  const webSiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "NVIG",
    url: "https://www.newvgroup.com",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://www.newvgroup.com/",
      },
    ],
  };

  return (
    <div>
      <SchemaMarkup
        data={[organizationSchema, webSiteSchema, breadcrumbSchema]}
      />
      <HeroSection />
      <div className="content-container my-20 mobile:my-40">
        <WhatWeDoSection />
        <Partnerships />
        <PublicationSection />
        <NewsHighlightSection />
      </div>
      <ContactUsBanner />
    </div>
  );
};

export default HomePage;
